import React, { useState, useEffect, useRef } from 'react';
import {
  subscribeToChatMessages,
  saveChatMessage,
  updateChatStatus,
  subscribeToChatStatus
} from '../services/firestoreService';
import './Dashboard.css';

const ChatWindow = ({ onClose, employeeData, allMessages = [] }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chatStatus, setChatStatus] = useState({ status: 'offline', lastSeen: null });

  // Get extension number from employeeData
  const extensionNumber = employeeData?.extensionNumber || employeeData?.extension || employeeData?.exten || employeeData?.ext;

  // Reference for messages container to enable auto-scroll
  const messagesEndRef = useRef(null);
  // Ref to store latest messages for cleanup
  const messagesRef = useRef([]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Use allMessages prop if available, otherwise subscribe
  useEffect(() => {
    if (allMessages && allMessages.length >= 0) {
      // Use messages from parent component (already subscribed)
      setMessages(allMessages);
      messagesRef.current = allMessages; // Store in ref for cleanup
      setLoading(false);
    } else if (extensionNumber) {
      // Fallback: subscribe if allMessages not provided
      setLoading(true);
      const unsubscribe = subscribeToChatMessages(extensionNumber, (chatMessages) => {
        setMessages(chatMessages);
        messagesRef.current = chatMessages; // Store in ref for cleanup
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [allMessages, extensionNumber]);

  // Subscribe to chat status changes
  useEffect(() => {
    if (!extensionNumber) return;

    // Set status to online when chat window opens
    updateChatStatus(extensionNumber, 'online').catch(error => {
      console.error('Error updating chat status to online:', error);
    });

    // Subscribe to status changes
    const unsubscribe = subscribeToChatStatus(extensionNumber, (statusData) => {
      setChatStatus(statusData);
    });

    return () => {
      // Cleanup: Set status to offline when component unmounts (chat closes)
      unsubscribe();
      if (extensionNumber) {
        // Use messages from ref to get the latest messages at unmount time
        const currentMessages = messagesRef.current || messages;
        if (currentMessages.length > 0) {
          // Get the last message timestamp
          const lastMessage = currentMessages[currentMessages.length - 1];
          const lastMessageTime = lastMessage.timestamp instanceof Date 
            ? lastMessage.timestamp 
            : new Date(lastMessage.timestamp || lastMessage.dateTime);
          updateChatStatus(extensionNumber, 'offline', lastMessageTime).catch(error => {
            console.error('Error updating chat status to offline:', error);
          });
        } else {
          // No messages, use current time
          updateChatStatus(extensionNumber, 'offline', new Date()).catch(error => {
            console.error('Error updating chat status to offline:', error);
          });
        }
      }
    };
  }, [extensionNumber]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !extensionNumber) return;

    const messageText = inputMessage.trim();
    setInputMessage('');

    try {
      // Save message to Firestore (using new structure)
      // The real-time listener will automatically update the UI when the message is saved
      await saveChatMessage(extensionNumber, messageText, 'admin');
    } catch (error) {
      console.error('Error saving chat message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  // Determine chat title from messages (get the "from" name of the other party)
  const getChatTitle = () => {
    if (messages.length === 0) {
      return 'Support Chat';
    }
    // Find a message that's not from the current user
    const otherPartyMessage = messages.find(msg => msg.from !== extensionNumber);
    if (otherPartyMessage) {
      return otherPartyMessage.from === 'admin' ? 'Admin' : otherPartyMessage.from;
    }
    return 'Support Chat';
  };

  // Format status display
  const getStatusDisplay = () => {
    if (chatStatus.status === 'online') {
      return 'Online';
    } else if (chatStatus.lastSeen) {
      const lastSeenDate = chatStatus.lastSeen instanceof Date 
        ? chatStatus.lastSeen 
        : new Date(chatStatus.lastSeen);
      const now = new Date();
      const diffMs = now - lastSeenDate;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) {
        return 'Last seen just now';
      } else if (diffMins < 60) {
        return `Last seen ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return `Last seen ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffDays < 7) {
        return `Last seen ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else {
        return `Last seen ${lastSeenDate.toLocaleDateString()}`;
      }
    }
    return 'Offline';
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="chat-avatar">💬</div>
          <div>
            <div className="chat-title">{getChatTitle()}</div>
            <div className="chat-status" style={{ 
              color: chatStatus.status === 'online' ? '#10b981' : '#94a3b8',
              fontSize: '12px',
              fontWeight: 'normal'
            }}>
              {getStatusDisplay()}
            </div>
          </div>
        </div>
        <button className="chat-close-btn" onClick={onClose}>×</button>
      </div>
      <div className="chat-messages">
        {loading ? (
          <div className="chat-empty-state">
            <div className="chat-empty-icon">⏳</div>
            <p>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-empty-state">
            <div className="chat-empty-icon">💬</div>
            <p>Start a conversation with our support team</p>
          </div>
        ) : (
          messages.map(message => {
            // Determine if message is from user (their extension) or from admin/support
            const isUserMessage = message.from === extensionNumber;
            const senderName = isUserMessage 
              ? (employeeData?.name || employeeData?.employeeName || 'You')
              : (message.from === 'admin' ? 'Admin' : message.from);
            
            return (
              <div 
                key={message.messageId || message.id} 
                className={`chat-message ${isUserMessage ? 'user-message' : 'bot-message'}`}
              >
                <div className="message-content">
                  <div className="message-text">{message.message || message.text}</div>
                  <div className="message-time">
                    {(message.timestamp instanceof Date 
                      ? message.timestamp 
                      : (message.dateTime instanceof Date 
                        ? message.dateTime 
                        : new Date(message.timestamp || message.dateTime))
                    ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
        {isTyping && (
          <div className="chat-message bot-message">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
      </div>
      <form className="chat-input-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          className="chat-input"
          placeholder="Type your message..."
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
        />
        <button type="submit" className="chat-send-btn" disabled={!inputMessage.trim()}>
          Send
        </button>
      </form>
    </div>
  );
};

export default ChatWindow;



